import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import type { Model } from '../models';
import { PromiseCompletion } from '../classes/PromiseCompletion';
import modelService from '../services/modelService';
import { defaulComparator, stringSearchService } from '../services/StringSearchService';
import { MemoryBar } from './MemoryBar';
import { ImgStar } from './Images/ImgStar';

type ModelSelectorProps = {
  model?: Model | null;
  onModelChange: (model: Model | null) => void;
}

@observer
class ModelSelector extends Component<ModelSelectorProps> {
  @observable private _isDropdownOpen: boolean = false;
  @observable private _searchFilter: string = '';
  @observable private _categoryFilters: Set<string> = new Set();
  @observable private _isControlPressed: boolean = false;
  private _refreshInterval: number | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _searchInputRef: React.RefObject<HTMLInputElement | null> = React.createRef();

  constructor(props: ModelSelectorProps) {
    super(props);
    makeObservable(this);
  }

  private async _loadModels(){
    await modelService.loadModels(this._loader);
    
    if (this.props.model) {
      return;
    }
    
    runInAction(() => {
      const savedModelName = localStorage.getItem('currentModel');
      let isModelSelected = false;
      if (savedModelName) {
        const [server, name] = savedModelName.split('@');
        const currentModel = modelService.models.find(model => model.server === server && model.name === name);
        if (currentModel) {
          isModelSelected = true;
          if (this.props.model?.server !== currentModel.server || this.props.model?.name !== currentModel.name) {
            this.props.onModelChange(currentModel);
          }
        }
      }

      if (!isModelSelected && modelService.models.length) {
        this.props.onModelChange(modelService.models[0]);
      }
    });
  }

  componentDidMount() {
    void this._loadModels();
    document.addEventListener('click', this._onDocumentClick);
    this._refreshInterval = window.setInterval(() => {
      void modelService.loadActiveModels();
    }, 1000 * 60 * 1); // Refresh every 1 minutes
    void modelService.loadActiveModels();

    document.addEventListener('modelsReloaded', this._onModelsReloaded);
    document.addEventListener('visibilitychange', this._onDocumentVisibilitychange);

    //handle shift pressed to show extra info in model selector
    document.addEventListener('keydown', this._onDocumentKeyDown);
    document.addEventListener('keyup', this._onDocumentKeyUp);
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this._onDocumentClick);
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
    document.removeEventListener('modelsReloaded', this._onModelsReloaded);
    document.removeEventListener('visibilitychange', this._onDocumentVisibilitychange);
    document.removeEventListener('keydown', this._onDocumentKeyDown);
    document.removeEventListener('keyup', this._onDocumentKeyUp);
  }

  @action
  private _onDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      this._isControlPressed = true;
    }
  }

  @action
  private _onDocumentKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      this._isControlPressed = false;
    }
  }

  @action
  private _onDocumentVisibilitychange = () => {
    if (document.visibilityState === 'visible') {
      this._loadModels();
      modelService.loadActiveModels();
    }
  };

  @action
  private _onModelsReloaded = async () => {
    await this._loadModels();
    await modelService.loadActiveModels();
  };

  @action
  private _onDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!this._isDropdownOpen || target.closest('.model-selector')) {
      return;
    }
    this._isDropdownOpen = false;
    this._searchFilter = '';
  };

  @action
  private _onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this._searchFilter = event.target.value;
  };

  @action
  private _onCategoryFilterToggle = (category: string) => {
    if (this._categoryFilters.has(category)) {
      this._categoryFilters.delete(category);
    } else {
      this._categoryFilters.add(category);
    }
  };

  @computed
  get searchTerms(){
      const searchStr = this._searchFilter;
      return searchStr.split(/[ ,]+/).filter(x => !!x);
  }

  @action
  private _clearFilters = () => {
    this._searchFilter = '';
    this._categoryFilters.clear();
  };

  private _getFilteredModels = (): Model[] => {
    let filteredList = modelService.models.filter(m => m.capabilities.includes('completion'));

    // Apply search filter
    const searchStringParts = this.searchTerms;
    if (searchStringParts.length) {
      filteredList = filteredList.filter((power): boolean => {
        let result = true;

        for(const term of searchStringParts){
          let anyMatch = false;

          if (defaulComparator(power.name, term, 0) !== -1){
              anyMatch = true;
          }

          if (defaulComparator(power.server, term, 0) !== -1){
              anyMatch = true;
          }
          if (!anyMatch){
              result = false;
          }
        }

        return result;
      });
    }

    // Apply category filters
    if (this._categoryFilters.size > 0) {
      filteredList = filteredList.filter(model => {
        return Array.from(this._categoryFilters).every(category => 
          model.capabilities.includes(category)
        );
      });
    }

    return filteredList.filter(x => !x.isHidden);
  };

  render() {
    const models = modelService.models.filter(x => !x.isHidden);
    if (this._loader.isPending && !models.length) {
      return <div>Loading models...</div>;
    }

    if (models.length === 0) {
      return <div>No models available</div>;
    }

    const { model } = this.props;
    const filteredModels = this._getFilteredModels();
    const hasActiveFilters = this._searchFilter.trim() || this._categoryFilters.size > 0;
    const uniqueCapabilities: Set<string> = new Set();
    for(const m of models){
      for(const c of m.capabilities){
        uniqueCapabilities.add(c);
      }
    }
    const categoryFilters = ['thinking', 'vision', 'tools'].filter(c => uniqueCapabilities.has(c));

    const activeModel = modelService.activeModels.find(activeModel =>
      activeModel.server === model?.server && activeModel.name === model?.name
    );
    const modelCard = modelService.models.find(m => m.server === model?.server && m.name === model?.name);
    const contextUsed = modelCard && activeModel ? (activeModel.context_length / modelCard.context_length) * 100 : 0;
    return (
      <div className="model-selector">
          {contextUsed > 0 && <div className="model-context-size-indicator-bar"
            style={{ width: `${contextUsed}%` }}>
          </div>}
          <div className="model-dropdown" onClick={this._onToggleModelDropdownClick}>
              {this._renderModel(model)}
              <span>▼</span>
          </div>
          <div className={'dropdown-menu' + (this._isDropdownOpen ? ' show' : '')}>
              {/* Search and Filter Controls */}
              <div className="dropdown-filters">
                <div className="search-container">
                  <input
                    ref={this._searchInputRef}
                    type="text"
                    placeholder="Search models..."
                    value={this._searchFilter}
                    onChange={this._onSearchChange}
                    className="search-input"
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                
                {(!!categoryFilters.length || hasActiveFilters) && <div className="category-filters">
                  {categoryFilters.map(category => (
                    <button
                      key={category}
                      className={`filter-btn ${this._categoryFilters.has(category) ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        this._onCategoryFilterToggle(category);
                      }}
                    >
                      {category}
                    </button>
                  ))}
                  
                  {hasActiveFilters && (
                    <button 
                      className="clear-filters-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        this._clearFilters();
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>}

              </div>

              {/* Model List */}
              <div className="dropdown-items">
                {this._loader.isPending && <div className="loading-state">Loading models...</div>}
                {filteredModels.length === 0 && !this._loader.isPending && (
                  <div className="no-results">No models match your filters</div>
                )}
                {filteredModels.map((model, index) => {
                  if (!activeModel && !model.capabilities.includes('completion')){
                    return null; // Skip models that do not support completion
                  }

                  let cssClass = 'dropdown-item';
                  if (model.server === this.props.model?.server && model.name === this.props.model?.name) {
                    cssClass += ' active';
                  }

                  return (
                    <div className={cssClass} key={index} onClick={() => this._onSelectModel(model)}>
                      {this._renderModel(model)}
                    </div>
                  );
                })}
              </div>
          </div>
      </div>
    );
  }

  private _renderText = (text: string | undefined | null) => {
    const { searchTerms } = this;

    const parts = stringSearchService.markMatches(text, searchTerms, defaulComparator);
    return (<>
      {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
    </>);
  };

  private _renderModel(model?: Model | null) {
    const modelName = model ? modelService.getModelName(model) : '';

    const activeModel = modelService.activeModels.find(activeModel =>
      activeModel.server === model?.server && activeModel.name === model?.name
    );

    const isFavorite = model?.isFavorite;

    let activeParameters: string | null = null;
    //get via regex the active parameters like a3b or a11B from model name
    if (model && model.name) {
      const match = model.name.match(/([aA]\d+[bB])/);
      if (match) {
        activeParameters = match[1];
      }
    }
    
    return (
      <div className={`model-info ${model?.isDisabled ? 'disabled' : ''}`}>
        <div className="model-name">
          {this._renderText(modelName)}
          {isFavorite && <span className="favorite-star" title="Favorite Model"><ImgStar /></span>}
        </div>
        <div className="model-capabilities">
          {model?.capabilities.filter(c => c === 'vision' || c === 'tools' || c === 'thinking').map((capability, i) => (
            <span className="capability-tag" key={i}>{capability}</span>
          ))}
          {model?.performanceLevel === 'partial' && <span className="capability-tag">🐌</span>}
          {activeParameters && <span className="capability-tag">{activeParameters}</span>}
          {/* round index to number without decimal places */}
          {this._isControlPressed && model?.artificial_analysis_intelligence_index && <span title="Ai Intelligence Index" className="capability-tag capability-tag-aii-index">{model.artificial_analysis_intelligence_index.toFixed(0)}</span>}
          {this._isControlPressed && model?.artificial_analysis_coding_index && <span title="Ai Coding Index" className="capability-tag capability-tag-aic-index">{model.artificial_analysis_coding_index.toFixed(0)}</span>}
          {modelService.prefetchingModel && model?.name === modelService.prefetchingModel && <span className="capability-tag">↺</span>}
        </div>
        {activeModel && <MemoryBar vram={activeModel.size_vram} total={activeModel.size} contextLength={activeModel.context_length} />}
      </div>
    );
  }

  @action
  private _onToggleModelDropdownClick = () => {
    this._isDropdownOpen = !this._isDropdownOpen;
    if (this._isDropdownOpen && this._searchInputRef) {
      // Focus search input when dropdown opens
      setTimeout(() => this._searchInputRef.current?.focus(), 100);
    }
  };

  @action
  private _onSelectModel = (model: Model) => {
    this.props.onModelChange(model);
    localStorage.setItem('currentModel', model.server + '@' + model.name);
    this._isDropdownOpen = false;
    this._searchFilter = '';
  };

  @action
  public trySetModel(modelName: string) {
    const model = modelService.models.find(m => m.name === modelName || (m.server + '@' + m.name) === modelName);
    if (model) {
      this.props.onModelChange(model);
      localStorage.setItem('currentModel', model.server + '@' + model.name);
      return true;
    }
    return false;
  }

  @action
  public trySetActiveModelIfGoodAvailable(): boolean {
    const activeModels = modelService.activeModels;
    const bestModels = ['qwen3.5:35b', 'qwen3.5:27b', 'qwen3.5:122b', 'gpt-oss:120b'];
    for(const bestModel of bestModels){
      const activeModel = activeModels.find(m => m.name === bestModel);
      if (activeModel) {
        const model = modelService.models.find(m => m.server === activeModel.server && m.name === activeModel.name);
        if (model){
          this.props.onModelChange(model);
          localStorage.setItem('currentModel', activeModel.server + '@' + activeModel.name);
          return true;
        }
      }
    }

    return false;
  }

  public setModelWithCapability(capability: string, bestModels: string[]) {
    //search models with the given capability, given priority to the bestModels in order they are passed, first one is the best
    const models = modelService.models.filter(m => m.capabilities.includes(capability));
    
    //order models by bestModels priority
    models.sort((a, b) => {
      const aIndex = bestModels.indexOf(a.name);
      const bIndex = bestModels.indexOf(b.name);
      if (aIndex === -1 && bIndex === -1) return 0; // neither model is in the bestModels list
      if (aIndex === -1) return 1; // a is not in the list, b is better
      if (bIndex === -1) return -1; // b is not in the list, a is better
      return aIndex - bIndex; // both are in the list, order by index
    });

    const bestModel = models[0];
    if (bestModel) {
      this.props.onModelChange(bestModel);
      return true;
    }
    return false;
  }
}

export default ModelSelector;